<template>
  <PillVariation
    :variations="variations"
    :preselected-variation="variationLabel"
    :disabled="disabled"
    @set-variation="setVariation"
  />
</template>

<script>
import { computed } from 'vue';
import viewFiltersMixin from '@/mixins/viewFiltersMixin';
import PillVariation from './PillVariation.vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import { useMyWorkMemberRoute } from '@/utilities/composables/useMyWorkMemberRoute';

export default {
  name: 'FilterVariation',

  components: {
    PillVariation,
  },

  mixins: [viewFiltersMixin],

  props: {
    type: {
      type: String,
      required: true,
      default: '',
    },
  },

  setup(props) {
    const { isMyWorkOrMemberRoute, isMyWorkAssignedTab, isMyWorkCreatedTab } =
      useMyWorkMemberRoute();
    const { embeddedBoardAttributes, embed } = useBoardEmbed();

    const disabled = computed(() => {
      return (
        isMyWorkOrMemberRoute.value &&
        // disabled for assigned to me filter in my-work/assigned tab
        ((isMyWorkAssignedTab.value && props.type === 'members') ||
          // and disabled for creator filter in my-work/created tab
          (isMyWorkCreatedTab.value && props.type === 'creators'))
      );
    });

    return {
      isMyWorkOrMemberRoute,
      embeddedBoardAttributes,
      embed,
      isMyWorkAssignedTab,
      isMyWorkCreatedTab,
      disabled,
    };
  },

  data() {
    return {
      selectedVariation: '',
    };
  },

  computed: {
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes?.boardId;
      }
      return this.$store.getters.currentBoardId;
    },
    searchModal() {
      return this.$store.getters.showSearchModal;
    },

    isDateFilter() {
      return ['time_created', 'due_date', 'time_updated'].includes(this.type);
    },

    isExclusion() {
      if (this.multivalueFilter || this.isTextFilter) {
        if (
          this.currentFilters?.exclude?.[this.type] &&
          this.currentFilters?.exclude?.[this.type].length
        ) {
          return true;
        }

        return (
          (this.type === 'members' && this.currentFilters.has_members) ||
          (this.type === 'statuses' && this.currentFilters.has_status)
        );
      }
      if (this.isDateFilter) {
        return this.currentFilters?.[this.type]?.after;
      }

      return !this.currentFilters?.[this.type];
    },

    multivalueFilter() {
      return [
        'creators',
        'owners',
        'members',
        'tags',
        'external_links',
        'statuses',
        'boards',
        'projects',
        'linked_cards',
        'priority',
        'estimates',
        'type',
        'parent_cards',
        'epics',
      ].includes(this.type);
    },

    singleValueFilter() {
      return [
        'is_parent',
        'is_child',
        'is_archived',
        'due_date',
        'time_created',
        'time_updated',
      ].includes(this.type);
    },

    isTextFilter() {
      return ['title'].includes(this.type);
    },

    variations() {
      if (this.multivalueFilter) {
        return [
          {
            id: 'includes',
            label: this.translate('includes'),
          },
          {
            id: 'excludes',
            label: this.translate('excludes'),
          },
        ];
      }
      if (this.isDateFilter) {
        const { before, after } = this.currentFilters.due_date || {};
        const overdueOrNoDueDate =
          (this.type === 'due_date' && ['now', '0'].includes(before || after)) ||
          (this.type === 'due_date' &&
            !this.currentFilters.has_due_date &&
            !this.currentFilters.due_date);

        return [
          {
            id: 'before',
            label: overdueOrNoDueDate ? this.translate('is') : this.translate('before'),
          },
          {
            id: 'after',
            label: overdueOrNoDueDate ? this.translate('is_not') : this.translate('after'),
          },
        ];
      }
      if (this.isTextFilter) {
        return [
          {
            id: 'contains',
            label: this.translate('contains'),
          },
        ];
      }
      return [
        {
          id: 'is',
          label: this.translate('is'),
        },
        {
          id: 'is_not',
          label: this.translate('is_not'),
        },
      ];
    },

    isDueDateUnset() {
      return (
        this.type === 'due_date' &&
        !this.currentFilters.has_due_date &&
        !this.currentFilters.due_date
      );
    },

    textVariationLabel() {
      if (this.currentFilters.include?.[this.type]) {
        return this.translate('contains');
      }
      return this.translate('not_contain');
    },

    dateFilterLabel() {
      const isOverdue = ['now', '0'].includes(this.currentFilters[this.type]?.before);
      const isNotOverdue = ['now', '0'].includes(this.currentFilters[this.type]?.after);

      if (['is', 'before'].includes(this.selectedVariation)) {
        return this.translate(isOverdue || this.isDueDateUnset ? 'is' : 'before');
      }

      return this.translate(isNotOverdue || this.isDueDateUnset ? 'is_not' : 'after');
    },

    variationLabel() {
      if (this.multivalueFilter) {
        return this.translate(this.selectedVariation);
      }

      if (this.singleValueFilter) {
        if (this.isDateFilter) {
          return this.dateFilterLabel;
        }
        if (this.selectedVariation === 'is') {
          return this.translate('is');
        }
        return this.translate('is_not');
      }
      if (this.isTextFilter) {
        return this.textVariationLabel;
      }
      return this.translate('is');
    },

    currentFilters() {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        return this.$store.getters.getViewFilters;
      }

      return this.$store.getters.boardFilters(this.currentBoardId) || {};
    },
  },
  watch: {
    isExclusion(newValue) {
      if (newValue) {
        this.selectedVariation = this.multivalueFilter ? 'excludes' : 'is_not';
      } else {
        this.selectedVariation = this.multivalueFilter ? 'includes' : 'is';
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      if (this.isExclusion) {
        if (this.isDateFilter) {
          this.selectedVariation = 'after';
        } else {
          this.selectedVariation = this.multivalueFilter ? 'excludes' : 'is_not';
        }
      } else if (this.isDateFilter) {
        this.selectedVariation = 'before';
      } else if (this.isTextFilter) {
        this.selectedVariation = 'contains';
      } else {
        this.selectedVariation = this.multivalueFilter ? 'includes' : 'is';
      }
    });
  },

  methods: {
    setVariation(variation) {
      const filters = { ...this.currentFilters };
      this.selectedVariation = variation.id;

      if (
        this.isDateFilter &&
        this.type === 'due_date' &&
        !this.currentFilters?.has_due_date &&
        !this.currentFilters.due_date &&
        this.selectedVariation === 'after'
      ) {
        delete filters.has_due_date;
        filters[this.type] = { after: '0' };
        this.setFilters({ filters });
        return;
      }

      if (this.singleValueFilter) {
        if (this.selectedVariation === 'is_not') {
          filters[this.type] = false;
        }
        if (this.selectedVariation === 'is') {
          filters[this.type] = true;
        }
        if (this.selectedVariation === 'after') {
          if (filters?.due_date?.after === '0') {
            delete filters[this.type];
            this.setFilters({ filters });
            return;
          }
          if (filters?.[this.type].before) {
            filters[this.type].after = filters[this.type].before;
            delete filters[this.type].before;
          } else {
            delete filters[this.type].after;
          }
          if (!Object.keys(filters[this.type]).length) {
            delete filters[this.type];
          }
        }
        if (this.selectedVariation === 'before') {
          if (!filters?.[this.type]) return;
          if (filters?.has_due_date === false) {
            delete filters.has_due_date;
            this.setFilters({ filters });
            return;
          }
          if (filters?.[this.type]?.after) {
            if (this.isDateFilter && filters[this.type].after === '0') {
              filters.has_due_date = false;
            } else {
              filters[this.type].before = filters[this.type].after;
            }
            delete filters[this.type].after;
          } else {
            delete filters[this.type].before;
          }
          if (!Object.keys(filters[this.type]).length) {
            delete filters[this.type];
          }
        }
      }

      if (this.multivalueFilter) {
        if (filters.has_members !== undefined && this.type === 'members') {
          if (
            (this.selectedVariation === 'includes' && filters.has_members === false) ||
            (this.selectedVariation === 'excludes' && filters.has_members === true)
          ) {
            delete filters.has_members;
          } else {
            filters.has_members = !filters.has_members;
          }
        }

        if (filters.has_status !== undefined && this.type === 'statuses') {
          if (
            (this.selectedVariation === 'includes' && filters.has_status === false) ||
            (this.selectedVariation === 'excludes' && filters.has_status === true)
          ) {
            delete filters.has_status;
          } else {
            filters.has_status = !filters.has_status;
          }
        }

        if (this.selectedVariation === 'includes') {
          if (filters.exclude?.[this.type]) {
            filters.include[this.type] = filters.exclude[this.type];
            delete filters.exclude[this.type];
          } else {
            delete filters.include[this.type];
          }
        }

        if (this.selectedVariation === 'excludes') {
          if (filters.include?.[this.type]) {
            filters.exclude[this.type] = filters.include[this.type];
            delete filters.include[this.type];
          } else {
            delete filters.exclude[this.type];
          }
        }
      }

      this.setFilters({ filters });
    },

    setFilters({ boardId = this.currentBoardId, filters }) {
      if (this.isViewOpen || this.isMyWorkOrMemberRoute) {
        this.setViewFilters(filters);
        return;
      }

      this.$store.dispatch('setBoardFilters', { boardId, filters });
    },
  },
};
</script>
